// src/hooks/useScanner.ts
// Globaler Scan-Hook: (a) Wedge-Keydown (Primaerpfad), (b) BarcodeDetector-Kamera (Fallback).
// Input-Haertung: waehrend eine Maske aktiv ist, verhindert der Listener Autofokus-Probleme,
// indem er NICHT an ein Eingabefeld gebunden ist (R3 — Fokus-Verlust ist Fehlerquelle Nr. 1).
import { useEffect, useRef, useState, useCallback } from 'react'
import { createWedgeBuffer } from '@/lib/scan'

export function useWedgeScanner(onScan: (code: string) => void, enabled: boolean): void {
  const cb = useRef(onScan); cb.current = onScan
  useEffect(() => {
    if (!enabled) return
    const buf = createWedgeBuffer((code) => cb.current(code))
    const handler = (e: KeyboardEvent) => {
      // Nicht abfangen, wenn der Nutzer bewusst in einem Textfeld tippt
      const t = e.target as HTMLElement | null
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      if (typing) return
      if (e.key === 'Enter') e.preventDefault()
      buf.onKey(e.key, performance.now())
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled])
}

type BarcodeDetectorLike = { detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>> }

export function useCameraScanner(onScan: (code: string) => void) {
  const [active, setActive] = useState(false)
  const [supported] = useState(() => typeof (globalThis as Record<string, unknown>)['BarcodeDetector'] !== 'undefined')
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const stopRef = useRef<() => void>(() => {})
  const cb = useRef(onScan); cb.current = onScan

  const stop = useCallback(() => { stopRef.current(); setActive(false) }, [])

  const start = useCallback(async () => {
    if (!supported || !videoRef.current) return
    const Ctor = (globalThis as Record<string, unknown>)['BarcodeDetector'] as new (o: { formats: string[] }) => BarcodeDetectorLike
    const detector = new Ctor({ formats: ['qr_code', 'code_128', 'ean_13'] })
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
    videoRef.current.srcObject = stream
    await videoRef.current.play()
    setActive(true)
    let running = true
    stopRef.current = () => { running = false; stream.getTracks().forEach((t) => t.stop()) }
    const tick = async () => {
      if (!running || !videoRef.current) return
      try {
        const codes = await detector.detect(videoRef.current)
        if (codes[0]?.rawValue) { cb.current(codes[0].rawValue); stop(); return }
      } catch { /* Frame nicht lesbar — weiter */ }
      setTimeout(() => void tick(), 150)
    }
    void tick()
  }, [supported, stop])

  useEffect(() => () => stopRef.current(), [])
  return { videoRef, start, stop, active, supported }
}
